import React, { useState, useEffect, useCallback } from 'react';
import type { SectionComponent } from '@ext-bookmark/shared/types';
import { getComponent } from '../lib/storage';
import Section from './Section';

const SectionRenderer: React.FC<{
  component: SectionComponent;
  workspaceId: string;
  onDataChange: () => void;
  onEdit?: (component: SectionComponent) => void;
}> = ({ component: initialComponent, workspaceId, onDataChange, onEdit }) => {
  const [component, setComponent] = useState<SectionComponent>(initialComponent);

  useEffect(() => {
    setComponent(initialComponent);
  }, [initialComponent]);

  const handleDataChange = useCallback(async () => {
    onDataChange();
    const updatedComponent = await getComponent(workspaceId, initialComponent.id);
    if (updatedComponent && updatedComponent.componentType === 'section') {
      setComponent(updatedComponent as SectionComponent);
    }
  }, [workspaceId, initialComponent.id, onDataChange]);

  const section = {
    id: component.id,
    name: component.name,
    x: component.config.x,
    y: component.config.y,
    width: component.config.width,
    height: component.config.height,
    contentItems: component.contentItems,
    locked: component.config.locked || false,
    createdAt: component.createdAt,
    updatedAt: component.updatedAt,
  };

  return (
    <Section
      section={section}
      workspaceId={workspaceId}
      componentId={component.id}
      component={component}
      layoutLocked={false}
      onDataChange={handleDataChange}
      onEdit={(s) => {
        if (onEdit) {
          const updatedComponent: SectionComponent = {
            ...component,
            name: s.name,
            config: {
              ...component.config,
              x: s.x,
              y: s.y,
              width: s.width,
              height: s.height,
              locked: s.locked,
            },
            contentItems: s.contentItems,
          };
          onEdit(updatedComponent);
        }
      }}
    />
  );
};

export default SectionRenderer;
